/** One list of what to chase on payment applications: payments past their final date and variations the paying party has left unanswered, longest wait first.
 *
 *  Reuses the predicates the Valuations list and Home already show, so the three can never disagree.
 */
import type { InterimApplication, InterimVariation } from '../../types/database';
import { readInterimFinancialColumns } from '../../utils/interimFinancialColumns';
import { getFinalDateCue } from './finalDateCue';
import { pendingDays } from './variationPending';

export interface ChaseItem {
  /** Stable across renders: the application for a payment, the variation reference for a variation. */
  id: string;
  kind: 'payment' | 'variation';
  /** Days overdue, or days pending. */
  days: number;
  title: string;
  projectId: string;
  /** The application to open when the row is chosen. */
  applicationId: string;
  /** The variation being chased, for a variation row; the chase log keys on it. */
  variationId?: string;
  /** What the message to the paying party says about this item, from the stored application. */
  detail: string;
  /** Payment rows only: the sum still owed, excluding VAT. Absent when the application states no figure. */
  amountOwed?: number;
  /** Payment rows with a figure: where the claim sits on its project's running total of sums stated due, and what has been received against it. Absent when the application stores no deduction. */
  claim?: ChaseClaim;
}

/** `from` is the sum the application deducted as previously stated due, `to` that plus its own stated sum. */
export interface ChaseClaim {
  from: number;
  to: number;
  received: number;
}

const plural = (n: number) => `${n} day${n === 1 ? '' : 's'}`;

const moneyFormat = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

/** "£12,000.00 excluding VAT". */
export const formatOwed = (amount: number): string => `${moneyFormat.format(amount)} excluding VAT`;

const toPence = (amount: number): number => Math.round(amount * 100);

/** Pence of the running total the claims cover, where they overlap counted once. */
function coveredOnce(claims: readonly ChaseClaim[]): number {
  const spans = claims.map((c) => [toPence(c.from), toPence(c.to)] as const).sort((a, b) => a[0] - b[0]);
  let covered = 0;
  let reach = -Infinity;
  for (const [from, to] of spans) {
    const start = Math.max(from, reach);
    if (to > start) covered += to - start;
    reach = Math.max(reach, to);
  }
  return covered;
}

/**
 * The sum owed across payment rows that state a figure, and how many rows that is; rows with no stated sum are left out rather than guessed.
 * An application that went out with nothing recorded against the one before it claims that money again (see isAwaitingOutcome), so adding
 * the rows would count it twice. Each project's claims are laid on its running total and counted once where they overlap; money received
 * changed hands whichever claim it was booked to, so all of it comes off.
 */
export function totalOwed(items: readonly ChaseItem[]): { amount: number; count: number } {
  const stated = items.filter((i) => i.amountOwed !== undefined);
  const claimsByProject = new Map<string, ChaseClaim[]>();
  let pence = 0;
  for (const item of stated) {
    if (item.claim) claimsByProject.set(item.projectId, [...(claimsByProject.get(item.projectId) ?? []), item.claim]);
    else pence += toPence(item.amountOwed ?? 0);
  }
  for (const claims of claimsByProject.values()) {
    pence += Math.max(0, coveredOnce(claims) - claims.reduce((sum, c) => sum + toPence(c.received), 0));
  }
  return { amount: pence / 100, count: stated.length };
}

/**
 * Where the owed sum sits, project by project, largest first. Each project goes through totalOwed on its own, so its overlap rules
 * hold and the lines add up to the overall figure. Projects with no stated sum are left out.
 */
export function owedByProject(items: readonly ChaseItem[]): Array<{ projectId: string; amount: number; count: number }> {
  const byProject = new Map<string, ChaseItem[]>();
  for (const item of items) byProject.set(item.projectId, [...(byProject.get(item.projectId) ?? []), item]);
  return [...byProject.entries()]
    .map(([projectId, rows]) => ({ projectId, ...totalOwed(rows) }))
    .filter((p) => p.count > 0)
    .sort((a, b) => b.amount - a.amount || a.projectId.localeCompare(b.projectId));
}

/** The notified sum less anything received. Undefined when no sum is stated, so the row shows no figure rather than a guess. */
function paymentOwed(raw: InterimApplication): { owed: number; claim?: ChaseClaim } | undefined {
  // A cleared or edited typed column wins over the stale figure left in the nested summary.
  const app = readInterimFinancialColumns(raw);
  const outcome = app.summary?.paymentOutcome;
  const stated = outcome?.status === 'pay_less_notice' ? outcome.sumStatedDue : app.summary?.amountDue;
  if (stated == null) return undefined;
  const received = outcome?.amountReceived ?? 0;
  const owed = Math.round((stated - received) * 100) / 100;
  if (owed <= 0) return undefined;
  const from = app.summary?.lessPreviouslyCertified;
  return { owed, ...(from == null ? {} : { claim: { from, to: from + stated, received } }) };
}

const issuedFormat = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', day: 'numeric', month: 'short', year: 'numeric' });

function variationDetail(v: InterimVariation, days: number): string {
  const issued = v.dateIssued ? new Date(v.dateIssued) : null;
  const when = issued && !Number.isNaN(issued.getTime()) ? ` on ${issuedFormat.format(issued)}` : '';
  const what = v.description?.trim() ? `${v.description.trim()}. ` : '';
  return `${what}It was issued${when} and has had no response for ${plural(days)}.`;
}

/** A plain-text reminder for the paying party, ready to paste into an email. The sender adds their own name. */
export function chaseMessage(item: ChaseItem, projectName: string): string {
  const subject = item.kind === 'payment' ? 'payment' : 'variation';
  const ask = item.kind === 'payment' ? 'Please confirm when payment will be made.' : 'Please confirm whether it is agreed.';
  return `Re: ${projectName}, ${subject}\n\n${item.title}. ${item.detail}\n\n${ask}\n\nKind regards`;
}

/** Drafts have put nothing to the paying party and rejected applications are closed, so neither is chased. */
function isChaseable(app: InterimApplication): boolean {
  return app.status !== 'draft' && app.status !== 'rejected';
}

export function buildChaseList(applications: readonly InterimApplication[], now: Date = new Date()): ChaseItem[] {
  const items: ChaseItem[] = [];
  // A variation carried across applications is one chase, counted at its earliest issue.
  const variations = new Map<string, ChaseItem>();

  applications.forEach((app) => {
    if (!isChaseable(app)) return;

    const cue = getFinalDateCue(app, now);
    if (cue?.tone === 'overdue' && cue.daysOverdue !== undefined) {
      const owing = paymentOwed(app);
      items.push({
        id: `payment-${app.id}`,
        kind: 'payment',
        days: cue.daysOverdue,
        title: `Application ${app.applicationNumber} unpaid, ${plural(cue.daysOverdue)} overdue`,
        projectId: app.projectId,
        applicationId: app.id,
        detail: `${cue.label}, so payment is now ${plural(cue.daysOverdue)} overdue.${owing === undefined ? '' : ` The sum outstanding is ${formatOwed(owing.owed)}.`}`,
        ...(owing === undefined ? {} : { amountOwed: owing.owed }),
        ...(owing?.claim === undefined ? {} : { claim: owing.claim }),
      });
    }

    (app.variations ?? []).forEach((v) => {
      const days = pendingDays(v.status, v.dateIssued, now);
      if (days === null) return;
      const ref = v.variationRef || v.id;
      const key = `${app.projectId}:${ref}`;
      if ((variations.get(key)?.days ?? -1) >= days) return;
      variations.set(key, {
        id: `variation-${key}`,
        kind: 'variation',
        days,
        title: `Variation ${ref} pending ${plural(days)}`,
        projectId: app.projectId,
        applicationId: app.id,
        variationId: v.id,
        detail: variationDetail(v, days),
      });
    });
  });

  return [...items, ...variations.values()].sort((a, b) => b.days - a.days || a.id.localeCompare(b.id));
}
